import React, { useCallback, useEffect, useRef, useState } from 'react';
import { addDays, fmtShort, key, parse, todayKey } from './dates.js';
import { fetchCalendar } from './api.js';
import AvailabilityCalendar from './AvailabilityCalendar.jsx';
import CalendarModal from './CalendarModal.jsx';

function inputMinDate(selector) {
  const el = document.querySelector(selector);
  return el && el.min ? el.min : todayKey();
}

function inputValue(selector) {
  const el = document.querySelector(selector);
  return el ? el.value : '';
}

export default function PropertyCalendar({
  slug,
  initialCheckIn = '',
  initialCheckOut = '',
  checkInInputSelector = 'input[name="checkIn"]',
  checkOutInputSelector = 'input[name="checkOut"]',
  title = 'Select dates',
  doneLabel = 'Save dates',
}) {
  const [open, setOpen] = useState(false);
  const [checkIn, setCheckIn] = useState(initialCheckIn);
  const [checkOut, setCheckOut] = useState(initialCheckOut);
  const [today, setToday] = useState(todayKey());
  const [data, setData] = useState(null);
  const [loadedUntil, setLoadedUntil] = useState('');
  const [error, setError] = useState(null);
  const fieldRef = useRef(null);

  useEffect(() => {
    setCheckIn(initialCheckIn);
    setCheckOut(initialCheckOut);
  }, [initialCheckIn, initialCheckOut]);

  useEffect(() => {
    // booking.js may have set default dates after the server-rendered prefills.
    // Sync from the native inputs so the summary field matches the form.
    setToday(inputMinDate(checkInInputSelector));
    const inVal = inputValue(checkInInputSelector);
    const outVal = inputValue(checkOutInputSelector);
    if (inVal && !checkIn) setCheckIn(inVal);
    if (outVal && !checkOut) setCheckOut(outVal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Availability loads in windows: ~8 months up front so the modal opens
  // fast, then further 12-month windows as the calendar navigates outward
  // (handleNeedMonths). loadedUntil is the last day with known availability;
  // anything past it must be treated as unknown, never as free.
  const loadedUntilRef = useRef('');
  const fetchingRef = useRef(false);

  useEffect(() => {
    const from = todayKey();
    const to = addDays(from, 238); // ~8 months
    fetchCalendar(slug, from, to)
      .then((d) => {
        setData(d);
        loadedUntilRef.current = to;
        setLoadedUntil(to);
      })
      .catch((err) => setError(err.message));
  }, [slug]);

  const handleNeedMonths = useCallback(
    (offset) => {
      const t = parse(today);
      const neededEnd = key(new Date(t.getFullYear(), t.getMonth() + offset + 1, 0));
      if (!loadedUntilRef.current || loadedUntilRef.current >= neededEnd || fetchingRef.current) return;
      fetchingRef.current = true;
      const from = addDays(loadedUntilRef.current, 1);
      const to = addDays(from, 364); // the API caps each request at ~18 months
      fetchCalendar(slug, from, to)
        .then((d) => {
          setData((prev) => ({
            ...d,
            minNightsByDate: { ...(prev?.minNightsByDate || {}), ...(d.minNightsByDate || {}) },
            blocked: Array.from(new Set([...(prev?.blocked || []), ...(d.blocked || [])])),
          }));
          loadedUntilRef.current = to;
          setLoadedUntil(to);
          fetchingRef.current = false;
          handleNeedMonths(offset); // still short of what was requested? chain the next window
        })
        .catch(() => {
          fetchingRef.current = false; // the next navigation retries
        });
    },
    [slug, today]
  );

  function openCalendar() {
    setOpen(true);
  }

  function writeInputs(inDate, outDate) {
    const inInput = document.querySelector(checkInInputSelector);
    const outInput = document.querySelector(checkOutInputSelector);
    if (inInput) {
      inInput.value = inDate;
      inInput.dispatchEvent(new Event('input', { bubbles: true }));
    }
    if (outInput) {
      outInput.value = outDate;
      outInput.dispatchEvent(new Event('input', { bubbles: true }));
    }
    return inInput;
  }

  function handleChange(inDate, outDate) {
    setCheckIn(inDate);
    setCheckOut(outDate);
    if (!inDate && !outDate) {
      // "Clear dates" in the modal - propagate immediately so a stale quote
      // never survives a cleared selection.
      const inInput = writeInputs('', '');
      const panel = inInput && inInput.closest('.book-panel');
      if (panel) panel.dispatchEvent(new CustomEvent('shs:dates-cleared', { bubbles: true }));
    }
  }

  function handleDone() {
    const inInput = writeInputs(checkIn, checkOut);
    setOpen(false);
    // Saving a full range runs the availability/price check straight away -
    // no separate "Check availability" press needed.
    if (checkIn && checkOut) {
      const form = inInput && inInput.closest('form');
      if (form) form.requestSubmit();
    }
  }

  const valueStyle = (filled) => ({
    fontSize: '14px',
    fontWeight: filled ? 600 : 400,
    color: filled ? '#2B2118' : '#B6A794',
  });

  return (
    <>
      <div className="shs-property-calendar-field" ref={fieldRef} onClick={openCalendar}>
        <div className="shs-property-calendar-cell">
          <span className="shs-property-calendar-label">Check in</span>
          <span style={valueStyle(!!checkIn)}>{checkIn ? fmtShort(checkIn) : 'Add date'}</span>
        </div>
        <div className="shs-property-calendar-cell">
          <span className="shs-property-calendar-label">Check out</span>
          <span style={valueStyle(!!checkOut)}>{checkOut ? fmtShort(checkOut) : 'Add date'}</span>
        </div>
      </div>
      {error && (
        <div className="shs-property-calendar-error" role="alert">{error}</div>
      )}

      <CalendarModal open={open} onClose={() => setOpen(false)} anchorRef={fieldRef}>
        {error ? (
          <div className="shs-calendar-card">
            <div className="shs-calendar-status">{error}</div>
          </div>
        ) : !data ? (
          <div className="shs-calendar-card">
            <div className="shs-calendar-status">Loading calendar…</div>
          </div>
        ) : (
          <AvailabilityCalendar
            title={title}
            minNights={data.minNights || 1}
            minNightsByDate={data.minNightsByDate || {}}
            blocked={data.blocked || []}
            checkIn={checkIn}
            checkOut={checkOut}
            today={today}
            loadedUntil={loadedUntil}
            onNeedMonths={handleNeedMonths}
            doneLabel={doneLabel}
            onChange={handleChange}
            onDone={handleDone}
          />
        )}
      </CalendarModal>
    </>
  );
}
